/**
 * PaymentSuccessScreen Component
 *
 * Displays tier-appropriate success messaging after Stripe payment completion.
 * Handles both successful payments and cancelled/error states.
 */

import {
  AlertCircle,
  Check,
  Crown,
  RefreshCw,
  Sparkles,
  Zap,
} from "lucide-react";
import { motion } from "motion/react";
import { useEffect } from "react";

import { trackConversion, trackUpsellFunnel } from "../utils/analytics";

import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

export type PaymentStatus = "success" | "cancelled" | "error";

interface PaymentSuccessScreenProps {
  status: PaymentStatus;
  tier?: "priority" | "founding" | "free";
  sessionId?: string;
  onRetry?: () => void;
  onGoHome: () => void;
}

const TIER_CONFIG = {
  priority: {
    name: "Priority Access",
    price: "$1",
    numericPrice: 1,
    icon: Zap,
    color: "text-primary",
    bgColor: "bg-primary/20",
    benefits: [
      "Beta access when we launch",
      "2 exclusive beta invites for friends",
      "Priority waitlist position",
      "Early feature previews",
    ],
  },
  founding: {
    name: "Founding Member",
    price: "$5",
    numericPrice: 5,
    icon: Crown,
    color: "text-secondary",
    bgColor: "bg-secondary/20",
    benefits: [
      "Beta access when we launch",
      "3 months Premium free",
      "3 exclusive beta invites",
      "Founding Member badge",
      "Priority support",
    ],
  },
  free: {
    name: "Free Waitlist",
    price: "Free",
    numericPrice: 0,
    icon: Sparkles,
    color: "text-muted-foreground",
    bgColor: "bg-muted",
    benefits: ["Standard waitlist access", "Email updates on launch"],
  },
};

export function PaymentSuccessScreen({
  status,
  tier = "free",
  sessionId,
  onRetry,
  onGoHome,
}: PaymentSuccessScreenProps) {
  const tierConfig = TIER_CONFIG[tier];
  const Icon = tierConfig.icon;

  // Get numeric price for analytics from config (single source of truth)
  const numericPrice = tierConfig.numericPrice;

  useEffect(() => {
    if (status === "success" && tier !== "free") {
      trackConversion("waitlist_signup", tier, numericPrice, {
        session_id: sessionId,
      });
      trackUpsellFunnel("payment_complete", {
        tier,
      });
    } else if (status === "cancelled") {
      trackUpsellFunnel("payment_cancelled", { tier });
    }
  }, [status, tier, sessionId, numericPrice]);

  if (status === "cancelled") {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6 max-w-md"
        >
          <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-muted-foreground" />
          </div>

          <div>
            <h1 className="text-2xl font-bold mb-2">Payment Cancelled</h1>
            <p className="text-muted-foreground">
              No worries! You&apos;re still on the waitlist. You can upgrade
              anytime.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {onRetry && (
              <Button
                onClick={onRetry}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            <Button variant="outline" onClick={onGoHome}>
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6 max-w-md"
        >
          <div className="w-20 h-20 mx-auto bg-destructive/20 rounded-full flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>

          <div>
            <h1 className="text-2xl font-bold mb-2">Payment Failed</h1>
            <p className="text-muted-foreground">
              Something went wrong with your payment. Please try again or
              contact support.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {onRetry && (
              <Button
                onClick={onRetry}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            <Button variant="outline" onClick={onGoHome}>
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-6 max-w-md w-full"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className={`w-20 h-20 mx-auto ${tierConfig.bgColor} rounded-full flex items-center justify-center`}
        >
          <Check className={`w-10 h-10 ${tierConfig.color}`} />
        </motion.div>

        {/* Main Message */}
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-bold mb-2"
          >
            {tier === "free"
              ? "You're on the waitlist!"
              : "Payment Successful!"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-muted-foreground"
          >
            {tier === "free"
              ? "Thanks for joining. We'll notify you when beta access is available."
              : `Welcome to ${tierConfig.name}! Check your email for confirmation.`}
          </motion.p>
        </div>

        {/* Tier Benefits Card */}
        {tier !== "free" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="glass-primary">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${tierConfig.bgColor}`}>
                      <Icon className={`w-5 h-5 ${tierConfig.color}`} />
                    </div>
                    <span className="font-semibold">{tierConfig.name}</span>
                  </div>
                  <span className={`text-lg font-bold ${tierConfig.color}`}>
                    {tierConfig.price}
                  </span>
                </div>

                <ul className="space-y-2 text-left">
                  {tierConfig.benefits.map((benefit, index) => (
                    <motion.li
                      key={benefit}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      className="flex items-center text-sm text-muted-foreground"
                    >
                      <Check className={`w-4 h-4 mr-2 ${tierConfig.color}`} />
                      {benefit}
                    </motion.li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-sm text-muted-foreground"
        >
          <p>📧 Check your email for confirmation and next steps.</p>
        </motion.div>

        {/* Home Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <Button
            onClick={onGoHome}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Back to Home
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
}
